import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import {
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  ORG,
} from "../lib/seo";

const SORO_EMBED_SRC = "https://app.trysoro.com/api/embed/d1429d19-09a1-4e43-b3ff-ecead9e9efeb";

/**
 * `/blog` — public blog page powered by the Soro embed.
 *
 * The Soro widget renders into <div id="soro-blog"></div>. We inject the
 * script tag exactly once on mount and remove it on unmount so re-navigating
 * to /blog (a likely behaviour while we're learning what content lands) keeps
 * working without duplicating the script.
 *
 * Pageviews are tracked automatically by the global RouteTracker in App.js —
 * no extra wiring needed here. Source attribution (Reddit, Google Ads, etc.)
 * carries through from the visitor's session, so we'll see exactly which
 * blog posts drive paid conversions later.
 */
export default function BlogPage() {
  useEffect(() => {
    document.title = "CFO Grade · Blog — Plain-English answers about your business numbers";
    upsertMeta(
      "name",
      "description",
      "Short, plain-English reads on the financial questions every business owner Googles at 1 a.m. — DSCR, EBITDA, runway, bankability, cash-flow timing."
    );
    upsertMeta(
      "property",
      "og:title",
      "CFO Grade · Blog"
    );
    upsertMeta(
      "property",
      "og:description",
      "Owner-friendly reads on the numbers behind your business."
    );
    upsertMeta("property", "og:url", `${window.location.origin}/blog`);
    upsertMeta("name", "twitter:title", "CFO Grade · Blog");
    upsertMeta(
      "name",
      "twitter:description",
      "Owner-friendly reads on the numbers behind your business."
    );
    upsertCanonical(`${window.location.origin}/blog`);

    // Blog + Breadcrumb schema — Soro loads posts client-side, so without
    // this Google sees /blog as a mostly empty shell. This tells crawlers
    // the page IS a blog and where it sits in the site hierarchy.
    const blogUrl = `${ORG.url}/blog`;
    upsertJsonLd("blog-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        {
          "@type": "Blog",
          "@id": `${blogUrl}#blog`,
          url: blogUrl,
          name: "CFO Grade Blog",
          description:
            "Short, plain-English reads on the financial questions every business owner Googles at 1 a.m. — DSCR, EBITDA, runway, bankability, cash-flow timing.",
          publisher: { "@id": `${ORG.url}#org` },
          inLanguage: "en-US",
        },
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Blog", url: blogUrl },
        ]),
      ],
    });

    // Inject Soro embed script exactly once. Guarded by an id so a remount
    // (React StrictMode or quick back/forward nav) won't pile up duplicates.
    const SCRIPT_ID = "soro-embed-script";
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SORO_EMBED_SRC;
      script.defer = true;
      document.body.appendChild(script);
    }

    return () => {
      // Remove the script + clear any rendered content so the next visit
      // re-runs the widget cleanly. (Soro re-evaluates on each insert.)
      const existing = document.getElementById(SCRIPT_ID);
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      const container = document.getElementById("soro-blog");
      if (container) container.innerHTML = "";
      // Clean up our JSON-LD so it doesn't leak into the next route's schema
      const jsonLd = document.getElementById("blog-graph");
      if (jsonLd) jsonLd.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain" data-testid="blog-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
          data-testid="blog-back-link"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 pt-4 pb-16">
        <div className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
            The CFO Grade blog
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-3">
            Plain-English answers
            <span className="italic text-[#C26D5C]"> about your numbers.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-5 max-w-2xl">
            Short reads on the questions every business owner has but rarely gets a straight
            answer to. DSCR, cash conversion, what "healthy" actually looks like, and how
            lenders read your books.
          </p>
        </div>

        {/* Soro embed mounts here */}
        <div
          id="soro-blog"
          data-testid="soro-blog-container"
          className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8 min-h-[400px]"
        />

        <p className="text-[12px] text-[#5C665D] mt-8 leading-relaxed">
          Want the read on your own numbers?{" "}
          <Link
            to="/analyze"
            className="underline underline-offset-2 hover:text-[#1F382E] transition-colors"
            data-testid="blog-cta-analyze"
          >
            Run a free CFO Grade in 5 minutes →
          </Link>
        </p>
      </main>

      <SiteFootnote />
    </div>
  );
}

function upsertMeta(attr, key, value) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Keep imports referenced even if a future redesign removes them — prevents
// false "unused import" lint errors from the bundled BrandMark export pair.
void BrandMark;
